import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@/components/ui/tooltip";
import { ArrowLeft, User } from "lucide-react";
import { toast } from "sonner";
import UploadExcelButton from "@/components/UploadExcelButton";
import { FbmStatTile, FBM_DESCRIPTIONS } from "@/components/FbmStat";
import DistributionChart from "@/components/DistributionChart";
import CareerChart from "@/components/CareerChart";
import TimelineChart from "@/components/TimelineChart";
import PlayerComparison from "@/components/PlayerComparison";
import TransferInfoBox from "@/components/TransferInfoBox";

const KEY_STATS = [
  ["minutes_played", "Min"],
  ["goals", "G"],
  ["assists", "A"],
  ["shots", "Sh"],
  ["shots_on_target", "SoT"],
  ["xg", "xG"],
  ["passes", "P"],
  ["passes_accurate", "P✓"],
];

function formatDob(dob) {
  if (!dob) return null;
  // Backend stores ISO date string; try to format as "12 Mar 1998"
  try {
    const d = new Date(dob);
    if (Number.isNaN(d.getTime())) return dob;
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return dob;
  }
}

// Phase 15 — FBM Replacement Value display. Three buckets: M, K, plain euros.
function formatReplacementValue(eur) {
  if (eur == null) return "N/A";
  if (eur >= 1_000_000) return `\u20AC${(eur / 1_000_000).toFixed(1)}M`;
  if (eur >= 1_000) return `\u20AC${Math.round(eur / 1_000)}K`;
  return `\u20AC${eur}`;
}

// Friendly labels for the `missing` array surfaced in the breakdown tooltip.
const MISSING_LABELS = {
  fbm_stats: "FBM stats",
  position: "Position",
  league_position: "League position",
  transfer_ceiling: "Transfer ceiling",
  height_cm: "Height",
  date_of_birth: "Date of birth",
  national_team: "National team",
  matches: "Matches",
};

function StatVal({ v }) {
  if (v == null || v === "") return <span className="text-muted-foreground">—</span>;
  if (typeof v === "number") return <span>{Number.isInteger(v) ? v : v.toFixed(2)}</span>;
  return <span>{String(v)}</span>;
}

export default function PlayerDetailPage({ basePath = "/admin", canImport = true }) {
  const { id } = useParams();
  const { user } = useAuth();
  const ignoreFriendlies = user?.ignore_friendlies ?? true;
  const [player, setPlayer] = useState(null);
  const [matches, setMatches] = useState({ total: 0, items: [] });
  const [fbm, setFbm] = useState(null);
  const [loading, setLoading] = useState(true);
  // Phase 11: page-level range selector that drives BOTH the FBM tiles and
  // the Distribution chart. The Comparison section keeps its OWN independent
  // selector (Phase 9), and the Career/Season Timeline charts are unaffected.
  //
  // Phase 12.1: default to the newest season label rather than "Whole Career".
  // We start with "career" so the very first fetch happens immediately; once
  // the DistributionChart bubbles up `available_ranges` we auto-switch to the
  // first non-career/non-year value (= newest season) for this player.
  const [range, setRange] = useState("career");
  const [autoRangePlayer, setAutoRangePlayer] = useState(null);
  // Phase 12 — transfer simulation
  const [transferSim, setTransferSim] = useState(null);
  const [transferActive, setTransferActive] = useState(false);
  const simOn = transferActive && transferSim && !transferSim.error && transferSim.posterior_final;
  const effectiveFbm = useMemo(() => {
    if (!simOn || !fbm) return fbm;
    const p = transferSim.posterior_final;
    return {
      ...fbm,
      overall: p.overall, finishing: p.finishing,
      passing: p.passing, defending: p.defending,
      fbm_score: transferSim.fbm_score_after,
    };
  }, [simOn, fbm, transferSim]);
  // eslint-disable-next-line no-unused-vars
  const _fbm_unused = fbm; // kept above; effectiveFbm shadows it below
  const [availableRanges, setAvailableRanges] = useState([
    { value: "career", label: "Whole Career" },
    { value: "year", label: "This Year" },
  ]);
  const [rangeLoading, setRangeLoading] = useState(false);

  // Phase 12.1: receive available_ranges from DistributionChart and auto-pick
  // the newest season the very first time we see ranges for this player.
  const handleAvailableRangesChange = (ranges) => {
    setAvailableRanges(ranges);
    if (autoRangePlayer !== id) {
      const newest = ranges.find((r) => r.value !== "career" && r.value !== "year");
      if (newest) {
        setRange(newest.value);
      }
      setAutoRangePlayer(id);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [p, m, f] = await Promise.all([
        api.get(`/players/${id}`),
        api.get(`/players/${id}/matches`, { params: { limit: 500 } }),
        api.get(`/players/${id}/fbm`, { params: { range } }).catch(() => ({ data: null })),
      ]);
      setPlayer(p.data);
      setMatches(m.data);
      setFbm(f.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  // Refetch whenever the player id changes OR the user toggles the
  // "ignore friendlies" preference (which alters /fbm and /matches results).
  useEffect(() => { load(); }, [id, ignoreFriendlies]);

  // Reset auto-range tracker so the next player starts on their newest season.
  // We DO NOT pre-reset `range` to "career" any more — we let the
  // DistributionChart bubble up available_ranges and auto-pick (see
  // handleAvailableRangesChange above). Keeping the old reset would briefly
  // refetch with "career" for every player switch.
  useEffect(() => { setAutoRangePlayer(null); }, [id]);

  // When `range` changes, refetch ONLY the FBM tiles (matches list and bio
  // are range-independent). The DistributionChart refetches itself via prop.
  useEffect(() => {
    let alive = true;
    setRangeLoading(true);
    api.get(`/players/${id}/fbm`, { params: { range } })
      .then((r) => { if (alive) setFbm(r.data); })
      .catch(() => {})
      .finally(() => { if (alive) setRangeLoading(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range, ignoreFriendlies]);

  const stableKeys = useMemo(() => KEY_STATS, []);

  if (loading) {
    return <div className="text-muted-foreground" data-testid="player-detail-loading">Loading…</div>;
  }
  if (!player) {
    return <div className="text-muted-foreground">Player not found.</div>;
  }

  return (
    <div data-testid="player-detail-page">
      <Link
        to={`${basePath}/players`}
        className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6"
        data-testid="player-detail-back"
      >
        <ArrowLeft className="h-4 w-4" /> Back to Players
      </Link>

      <header className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-muted-foreground mb-2">Player profile</div>
          <h1 className="font-display uppercase text-4xl md:text-5xl font-bold tracking-tight">{player.full_name}</h1>
          {(player.teams || []).length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5" data-testid="player-detail-team-badges">
              {player.teams.map((t) => (
                <Badge key={t.id} variant="secondary" className="rounded-none uppercase tracking-wider text-[10px]">{t.name}</Badge>
              ))}
            </div>
          )}
          {player.national_team_status && player.national_team_level && (
            <div className="mt-2" data-testid="player-detail-nt-badge-wrap">
              <Badge
                variant="outline"
                data-testid="player-detail-nt-badge"
                className={
                  "rounded-none uppercase tracking-wider text-[10px] " +
                  (player.national_team_status === "Current"
                    ? "border-emerald-500/60 text-emerald-500"
                    : "border-muted-foreground/40 text-muted-foreground")
                }
              >
                {player.national_team_status} — {player.national_team_level}
              </Badge>
            </div>
          )}
          {/* Phase 15 — FBM Replacement Value KPI. Visible to admin & scout.
              Hover reveals the breakdown (A1..A6, stats source, missing). */}
          <div className="mt-4" data-testid="player-detail-replacement-wrap">
            <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-1">
              FBM Replacement Value
            </div>
            <TooltipProvider delayDuration={150}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span
                    className="font-display text-2xl md:text-3xl font-bold tracking-tight cursor-help border-b border-dashed border-muted-foreground/30 inline-block"
                    data-testid="player-detail-replacement-value"
                  >
                    {formatReplacementValue(player.fbm_replacement_value)}
                  </span>
                </TooltipTrigger>
                <TooltipContent
                  side="bottom"
                  align="start"
                  data-testid="player-detail-replacement-tooltip"
                  className="bg-card text-foreground border border-border max-w-sm p-3 text-[11px] leading-relaxed"
                >
                  {(() => {
                    const br = player.fbm_replacement_value_breakdown || {};
                    const missing = br.missing || [];
                    if (missing.length > 0) {
                      return (
                        <div className="space-y-1">
                          <div className="text-muted-foreground uppercase tracking-wider text-[9px]">
                            Cannot compute — missing
                          </div>
                          <ul className="list-disc pl-4 text-foreground/85">
                            {missing.map((m) => (
                              <li key={m} data-testid={`replacement-missing-${m}`}>
                                {MISSING_LABELS[m] || m}
                              </li>
                            ))}
                          </ul>
                        </div>
                      );
                    }
                    const ss = br.stats_source || {};
                    return (
                      <div className="space-y-1 font-mono text-[11px]">
                        <div className="grid grid-cols-2 gap-x-3">
                          <span className="text-muted-foreground">A1 (ceiled FBM)</span>
                          <span>{br.A1}</span>
                          <span className="text-muted-foreground">A2 (league pos)</span>
                          <span>{br.A2?.toFixed(4)}</span>
                          <span className="text-muted-foreground">A3 (ceiling)</span>
                          <span>{br.A3}</span>
                          <span className="text-muted-foreground">A4 (nat. team)</span>
                          <span>{br.A4?.toFixed(2)}</span>
                          <span className="text-muted-foreground">A5 (height)</span>
                          <span>{br.A5?.toFixed(4)}</span>
                          <span className="text-muted-foreground">A6 (age)</span>
                          <span>{br.A6?.toFixed(2)}</span>
                        </div>
                        <div className="border-t border-border/60 pt-1 mt-1 text-muted-foreground font-sans uppercase tracking-wider text-[9px]">
                          Stats source
                        </div>
                        <div className="font-sans text-[11px]">
                          {(ss.seasons_used || []).join(" + ") || "—"}
                          <span className="text-muted-foreground"> · {ss.matches_used ?? 0} matches</span>
                        </div>
                      </div>
                    );
                  })()}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>
        {canImport && (
          <div className="flex items-center gap-2" data-testid="player-detail-actions">
            <UploadExcelButton
              url={`/players/${id}/import-excel`}
              testId="player-detail-upload"
              onResult={load}
            />
            <span className="text-sm text-muted-foreground hidden sm:inline">Upload Excel</span>
          </div>
        )}
      </header>

      {/* Player info panel — biographical fields */}
      <section className="mb-8" data-testid="player-info-section">
        <div className="rounded-xl border border-border bg-card p-6">
          <h2 className="font-display uppercase tracking-tight text-sm text-muted-foreground mb-4">Player info</h2>
          <dl className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-4 text-sm">
            <div data-testid="info-full-name">
              <dt className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Full name</dt>
              <dd className="mt-1 font-medium">{player.full_name}</dd>
            </div>
            <div data-testid="info-dob">
              <dt className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Date of birth</dt>
              <dd className="mt-1 font-mono">{formatDob(player.date_of_birth) || <span className="text-muted-foreground">—</span>}</dd>
            </div>
            <div data-testid="info-birth-nat">
              <dt className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Birth nationality</dt>
              <dd className="mt-1">{player.birth_nationality || <span className="text-muted-foreground">—</span>}</dd>
            </div>
            <div data-testid="info-passports">
              <dt className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Passports</dt>
              <dd className="mt-1">
                {(player.passport_nationalities || []).length
                  ? player.passport_nationalities.join(", ")
                  : <span className="text-muted-foreground">—</span>}
              </dd>
            </div>
            <div data-testid="info-foot">
              <dt className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Preferred foot</dt>
              <dd className="mt-1 capitalize">{player.preferred_foot || <span className="text-muted-foreground">—</span>}</dd>
            </div>
            <div data-testid="info-position">
              <dt className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Position</dt>
              <dd className="mt-1 font-mono">{player.position || <span className="text-muted-foreground font-sans">—</span>}</dd>
            </div>
            <div data-testid="info-height">
              <dt className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Height</dt>
              <dd className="mt-1 font-mono">{player.height_cm == null ? <span className="text-muted-foreground font-sans">—</span> : `${player.height_cm} cm`}</dd>
            </div>
            <div data-testid="info-weight">
              <dt className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Weight</dt>
              <dd className="mt-1 font-mono">{player.weight_kg == null ? <span className="text-muted-foreground font-sans">—</span> : `${player.weight_kg} kg`}</dd>
            </div>
            <div data-testid="info-teams">
              <dt className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Teams</dt>
              <dd className="mt-1 flex flex-wrap gap-1">
                {(player.teams || []).length === 0
                  ? <span className="text-muted-foreground">—</span>
                  : player.teams.map((t) => (
                      <Badge key={t.id} variant="outline" className="rounded-none text-[10px] uppercase tracking-wider">
                        {t.name}{t.country ? ` · ${t.country}` : ""}
                      </Badge>
                    ))}
              </dd>
            </div>
          </dl>
        </div>
      </section>

      {/* Phase 11 — page-level Range selector. Drives the FBM tiles AND
          the Probability Distribution chart below. The Career chart, the
          Season Timeline and the Player Comparison section are NOT
          affected by this selector. */}
      <section className="mb-4" data-testid="page-range-selector-section">
        <div className={`rounded-xl border border-border bg-card px-6 py-4 flex flex-wrap items-center justify-between gap-4 ${simOn ? "opacity-50 pointer-events-none" : ""}`}>
          <div className="flex items-center gap-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-1">Stats range</div>
              <Select value={range} onValueChange={setRange} disabled={simOn}>
                <SelectTrigger
                  className="h-10 min-w-[200px] font-medium"
                  data-testid="page-range-select"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {availableRanges.map((r) => (
                    <SelectItem
                      key={r.value}
                      value={r.value}
                      data-testid={`page-range-option-${r.value.replace("/", "-")}`}
                    >
                      {r.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="text-xs text-muted-foreground max-w-md leading-relaxed">
              Affects the <span className="text-foreground/85 font-medium">FBM tiles</span> and the
              <span className="text-foreground/85 font-medium"> Probability Distribution</span> chart below.
              The Career and Season Timeline charts are unaffected.
            </div>
          </div>
          {rangeLoading && (
            <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground" data-testid="page-range-loading">
              Updating…
            </span>
          )}
        </div>
        {simOn && (
          <div className="text-[11px] text-amber-500/80 mt-2 px-1" data-testid="range-disabled-note">
            Range selector disabled while transfer simulation is active.
          </div>
        )}
      </section>

      {/* Phase 12 — Transfer simulation infobox */}
      <section className="mb-8" data-testid="transfer-section">
        <TransferInfoBox
          playerId={id}
          playerName={player.full_name}
          active={transferActive}
          onActiveChange={setTransferActive}
          onSimulationChange={setTransferSim}
        />
      </section>

      {/* FBM tiles */}
      <section className="mb-10" data-testid="fbm-tiles-section">
        <div className="flex flex-wrap items-baseline justify-between gap-3 mb-4">
          <h2 className="font-display uppercase tracking-tight text-xl">FBM probabilities</h2>
          <div className="text-xs text-muted-foreground">
            {fbm?.inputs?.team_used_for_position
              ? <>Position-in-table from <span className="text-foreground">{fbm.inputs.team_used_for_position}</span>: <span className="font-mono">{fbm.inputs.position_in_table}</span></>
              : fbm
                ? <>Position-in-table (default): <span className="font-mono">{fbm.inputs.position_in_table}</span></>
                : null}
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <FbmStatTile label="Overall"   metric="overall"   value={effectiveFbm?.overall}   description={FBM_DESCRIPTIONS.overall}   testId="fbm-tile-overall" />
          <FbmStatTile label="Finishing" metric="finishing" value={effectiveFbm?.finishing} description={FBM_DESCRIPTIONS.finishing} testId="fbm-tile-finishing" />
          <FbmStatTile label="Passing"   metric="passing"   value={effectiveFbm?.passing}   description={FBM_DESCRIPTIONS.passing}   testId="fbm-tile-passing" />
          <FbmStatTile label="Defending" metric="defending" value={effectiveFbm?.defending} description={FBM_DESCRIPTIONS.defending} testId="fbm-tile-defending" />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
          <div className="rounded-xl border border-border bg-muted/30 p-5" data-testid="fbm-tile-minutes">
            <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-3">Minutes played</div>
            <div className="font-display text-4xl font-bold leading-none">{effectiveFbm?.minutes_played ?? "—"}</div>
          </div>
          <div className="rounded-xl border border-border bg-muted/30 p-5" data-testid="fbm-tile-score">
            <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-3">FBM score{simOn && <span className="ml-2 text-[10px] text-amber-500/80 uppercase tracking-wider">simulated</span>}</div>
            <div className="font-display text-4xl font-bold leading-none">{effectiveFbm?.fbm_score == null ? "—" : (Math.ceil(Number(effectiveFbm.fbm_score) * 10) / 10).toFixed(1)}<span className="text-xl opacity-50"> / 10</span></div>
          </div>
        </div>
      </section>

      {/* Probability distribution curves — controlled by the page-level range
          OR by the transfer simulation when active. */}
      <section className="mb-10" data-testid="fbm-distribution-section">
        <DistributionChart
          playerId={id}
          basePath={basePath}
          range={range}
          onAvailableRangesChange={handleAvailableRangesChange}
          playerName={player.full_name}
          valuesOverride={simOn ? transferSim.posterior_final : null}
          overrideLabel={simOn ? `Transfer to ${transferSim.new_team?.name}` : null}
        />
      </section>

      {/* Career timeline — one isolation point per season (Phase 11.3) */}
      <section className="mb-10" data-testid="fbm-career-section">
        <CareerChart playerId={id} playerName={player.full_name} />
      </section>

      {/* Season timeline */}
      <section className="mb-10" data-testid="fbm-timeline-section">
        <TimelineChart playerId={id} playerName={player.full_name} />
      </section>

      {/* Head-to-head comparison */}
      <section className="mb-10" data-testid="fbm-comparison-section">
        <PlayerComparison
          playerId={id}
          currentPlayerName={player.full_name}
          focusOverride={simOn ? transferSim.posterior_final : null}
          focusOverrideLabel={simOn ? `Transfer to ${transferSim.new_team?.name}` : null}
        />
      </section>

      <Card className="rounded-xl">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
          <CardTitle className="font-display uppercase tracking-tight text-xl">Matches</CardTitle>
          <Badge variant="secondary" className="rounded-none uppercase tracking-wider text-[10px]" data-testid="player-matches-count">
            {matches.total} match{matches.total === 1 ? "" : "es"}
          </Badge>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border border-border overflow-x-auto">
            <Table data-testid="player-matches-table">
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="font-display uppercase tracking-[0.15em] text-[11px]">Date</TableHead>
                  <TableHead className="font-display uppercase tracking-[0.15em] text-[11px]">Opponent</TableHead>
                  <TableHead className="font-display uppercase tracking-[0.15em] text-[11px]">Competition</TableHead>
                  <TableHead className="font-display uppercase tracking-[0.15em] text-[11px]">Score</TableHead>
                  {stableKeys.map(([k, label]) => (
                    <TableHead key={k} className="font-display uppercase tracking-[0.15em] text-[11px] text-right">{label}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {matches.items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4 + stableKeys.length} className="text-center py-12">
                      <User className="h-8 w-8 mx-auto mb-3 text-muted-foreground" />
                      <div className="text-muted-foreground">No matches yet. Upload an Excel to import.</div>
                    </TableCell>
                  </TableRow>
                ) : matches.items.map((m) => (
                  <TableRow key={m.id} className="tr-fade" data-testid={`player-match-row-${m.id}`}>
                    <TableCell className="text-muted-foreground whitespace-nowrap">{m.match_date}</TableCell>
                    <TableCell className="font-medium">{m.opponent_name || "—"}</TableCell>
                    <TableCell className="text-muted-foreground text-xs max-w-[12rem] truncate" title={m.competition_name}>{m.competition_name || "—"}</TableCell>
                    <TableCell className="font-mono whitespace-nowrap">
                      {m.home_score == null && m.away_score == null
                        ? <span className="text-muted-foreground">—</span>
                        : `${m.home_score ?? "?"} – ${m.away_score ?? "?"}`}
                    </TableCell>
                    {stableKeys.map(([k]) => (
                      <TableCell key={k} className="text-right font-mono">
                        <StatVal v={k === "minutes_played" ? m.minutes_played : m.stats?.[k]} />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
